import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Send, Bot, User, X } from 'lucide-react';
import { chatbotAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ChatBotProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ChatBot = ({ open: externalOpen, onOpenChange }: ChatBotProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [isMinimized] = useState(false);
  const [shouldHideButton, setShouldHideButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  useEffect(() => {
    const checkOverlays = () => {
      const drawerForms = document.querySelectorAll(
        '[data-drawer-form="true"]'
      );

      const sheetContent = document.querySelectorAll(
        '[data-radix-sheet-content]'
      );
      const drawerContent = document.querySelectorAll(
        '[data-radix-drawer-content]'
      );

      const openOverlays = document.querySelectorAll('[data-state="open"]');

      const modalDialogs = document.querySelectorAll('[role="dialog"]');

      const fixedElements = Array.from(
        document.querySelectorAll('.fixed')
      ).filter(el => {
        const rect = el.getBoundingClientRect();
        return (
          (rect.width > window.innerWidth * 0.8 ||
            rect.height > window.innerHeight * 0.8) &&
          !el.closest('[data-chatbot]')
        );
      });

      const isAnyOverlayOpen =
        drawerForms.length > 0 ||
        sheetContent.length > 0 ||
        drawerContent.length > 0 ||
        Array.from(openOverlays).some(el =>
          el.closest(
            '[data-radix-sheet-content], [data-radix-drawer-content], [data-radix-dialog-content]'
          )
        ) ||
        Array.from(modalDialogs).some(el => {
          const htmlEl = el as HTMLElement;
          return (
            htmlEl.style.display !== 'none' && el.closest('[data-state="open"]')
          );
        }) ||
        fixedElements.length > 0;

      setShouldHideButton(isAnyOverlayOpen);
    };

    checkOverlays();
    const interval = setInterval(checkOverlays, 200);

    return () => clearInterval(interval);
  }, []);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hello! I'm your Forex CRM assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setMessages([
        {
          id: '1',
          content:
            "Hello! I'm your Forex CRM assistant. How can I help you today?",
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    }
    setInputValue('');
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await chatbotAPI.sendMessage(messageToSend);

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response.reply,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to get response from chatbot. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleButtonClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {externalOpen === undefined && !shouldHideButton && (
        <Button
          onClick={handleButtonClick}
          className="fixed bottom-5 right-5 h-14 w-14 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 animate-bounce-pulse z-[100]"
          size="icon"
          data-chatbot="true"
        >
          {isOpen ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
        </Button>
      )}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <div
          className={isOpen ? 'animate-smooth-expand' : ''}
          data-chatbot="true"
        >
          <DialogContent
            className={`p-0 gap-0 ${
              isMinimized ? 'h-16' : 'h-[600px] sm:h-[500px]'
            } w-[calc(100%-2rem)] sm:w-[350px] md:w-[380px] fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-50 rounded-2xl overflow-hidden !left-auto !top-auto !translate-x-0 !translate-y-0 m-0`}
            closeIconColor="white"
            data-chatbot="true"
          >
            <DialogHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-2 !flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 bg-white/20">
                  <AvatarFallback className="bg-white/20 text-white">
                    <Bot className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <DialogTitle className="text-white text-lg font-semibold">
                    Forex CRM Assistant
                  </DialogTitle>
                  <p className="text-blue-100 text-xs">
                    Online • Ready to help
                  </p>
                </div>
              </div>
            </DialogHeader>

            {!isMinimized && (
              <>
                <div
                  className="flex-1 p-2 h-[calc(600px-140px)] overflow-y-auto"
                  ref={scrollAreaRef}
                >
                  <div className="space-y-4">
                    {messages.map(message => (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${
                          message.sender === 'user'
                            ? 'justify-end'
                            : 'justify-start'
                        }`}
                      >
                        {message.sender === 'bot' && (
                          <Avatar className="h-8 w-8 bg-blue-100 flex-shrink-0">
                            <AvatarFallback className="bg-blue-100 text-blue-600">
                              <Bot className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div
                          className={`max-w-[80%] rounded-2xl p-3 ${
                            message.sender === 'user'
                              ? 'bg-blue-600 text-white rounded-br-sm animate-fade-slide-up'
                              : 'bg-gray-100 text-gray-900 rounded-bl-sm animate-fade-slide-down'
                          }`}
                        >
                          <p className="text-sm leading-relaxed">
                            {message.content}
                          </p>
                        </div>
                        {message.sender === 'user' && (
                          <Avatar className="h-8 w-8 bg-blue-600 flex-shrink-0">
                            <AvatarFallback className="bg-blue-600 text-white">
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex gap-3 justify-start">
                        <Avatar className="h-8 w-8 bg-blue-100 flex-shrink-0">
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            <Bot className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="max-w-[80%] rounded-2xl p-3 bg-gray-100 text-gray-900 rounded-bl-sm">
                          <div className="flex items-center gap-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div
                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                style={{ animationDelay: '0.1s' }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                style={{ animationDelay: '0.2s' }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Input Area */}
                <div className="border-t p-2 bg-white">
                  <div className="flex gap-2">
                    <Input
                      value={inputValue}
                      onChange={e => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={isLoading}
                      className="bg-blue-600 hover:bg-blue-700 hover:animate-glow-pulse transition-all duration-300"
                      size="icon"
                    >
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Powered by Forex CRM AI
                  </p>
                </div>
              </>
            )}
          </DialogContent>
        </div>
      </Dialog>
    </>
  );
};

export default ChatBot;
